import { FC, useEffect, useMemo, useRef } from 'react'
import * as d3 from 'd3'
import { Box, Typography } from '@mui/material'
import theme from 'theme'
import { cssVar } from 'theme/config'

import useResizeObserver from 'hooks/useResizeObserver'
import { useThemeColors } from 'hooks/useThemeColors'
import { ExpenseTrendDataPoint, SSMNistPillar } from 'models'
import { formatCostCompact, formatCostFull, getNistPillarBackground } from '../../securityStackMap.utils'
import { ChartCard, ChartWrapper, ChartTooltip, LegendContainer, LegendItem } from '../ssmHistoricalOverview.styles'

const RECORDED_COLOR = cssVar('cta')
const ESTIMATED_COLOR = cssVar('expense-estimated')

const margin = { top: 20, right: 30, bottom: 40, left: 60 }

interface Props {
  data: ExpenseTrendDataPoint[]
  loading: boolean
}

const ExpenseTrendChart: FC<Props> = ({ data, loading }) => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const dimensions = useResizeObserver(wrapperRef)
  const getColor = useThemeColors()

  const hasData = useMemo(() => {
    return data.some((d) => d.expense > 0)
  }, [data])

  useEffect(() => {
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    if (!wrapperRef.current || !data.length || loading || !hasData) return

    const { width: containerWidth, height: containerHeight } = dimensions || wrapperRef.current.getBoundingClientRect()
    if (!containerWidth || !containerHeight) return
    const width = containerWidth - margin.left - margin.right
    const height = containerHeight - margin.top - margin.bottom

    svg.attr('width', containerWidth).attr('height', containerHeight)

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    const tooltip = d3.select(tooltipRef.current)

    /* Scales */
    const xScale = d3
      .scalePoint<string>()
      .domain(data.map((d) => d.label))
      .range([0, width])

    const yMax = d3.max(data, (d) => d.expense) || 0
    const yScale = d3
      .scaleLinear()
      .domain([0, yMax * 1.15])
      .range([height, 0])
      .nice()

    /* Grid lines */
    const yDomainValues = yScale.domain()
    const yTicks = [yDomainValues[0], (yDomainValues[0] + yDomainValues[1]) / 2, yDomainValues[1]]
    g.selectAll('.grid-line')
      .data(yTicks)
      .join('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', getColor('color-borderMedium'))
      .attr('stroke-dasharray', '4,4')
      .attr('stroke-width', 1)

    /* Split data into recorded and estimated segments */
    const recordedData = data.filter((d) => !d.isEstimated)
    const estimatedData = data.filter((d) => d.isEstimated)

    const lastRecorded = recordedData[recordedData.length - 1]
    const estimatedWithBridge = lastRecorded ? [lastRecorded, ...estimatedData] : estimatedData

    /* Recorded area background */
    if (lastRecorded) {
      const recordedEndX = xScale(lastRecorded.label) || 0
      g.append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', recordedEndX)
        .attr('height', height)
        .attr('fill', cssVar('expense-recorded-area'))
    }

    const lineGenerator = d3
      .line<ExpenseTrendDataPoint>()
      .x((d) => xScale(d.label) || 0)
      .y((d) => yScale(d.expense))

    /* Recorded line (solid) */
    if (recordedData.length > 0) {
      g.append('path')
        .datum(recordedData)
        .attr('d', lineGenerator)
        .attr('fill', 'none')
        .attr('stroke', RECORDED_COLOR)
        .attr('stroke-width', 2.5)
    }

    /* Estimated line (solid, different color) */
    if (estimatedWithBridge.length > 1) {
      g.append('path')
        .datum(estimatedWithBridge)
        .attr('d', lineGenerator)
        .attr('fill', 'none')
        .attr('stroke', ESTIMATED_COLOR)
        .attr('stroke-width', 2)
    }

    /* Hover elements - vertical line */
    const cursorLine = g
      .append('line')
      .attr('class', 'cursor-line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', getColor('text-primary'))
      .attr('stroke-width', 1)
      .style('display', 'none')

    /* Hover elements - outer circle (glow) */
    const hoverCircleOuter = g
      .append('circle')
      .attr('class', 'hover-circle-outer')
      .attr('r', 7)
      .attr('fill', 'none')
      .attr('stroke', getColor('text-primary'))
      .attr('stroke-width', 15)
      .attr('stroke-opacity', 0.5)
      .attr('display', 'none')

    /* Hover elements - inner circle */
    const hoverCircleInner = g
      .append('circle')
      .attr('class', 'hover-circle-inner')
      .attr('r', 7)
      .attr('fill', getColor('legacy-white-3'))
      .attr('stroke', getColor('legacy-000000'))
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 1)
      .attr('display', 'none')

    /* Precompute data point positions for hit detection */
    const positions = data.map((d) => ({
      x: xScale(d.label) || 0,
      y: yScale(d.expense),
      data: d
    }))

    /* Mouse interaction on SVG */
    svg.on('mousemove', (event) => {
      const [mouseX, mouseY] = d3.pointer(event, g.node())
      if (mouseX < 0 || mouseX > width || mouseY < 0 || mouseY > height) {
        cursorLine.style('display', 'none')
        hoverCircleInner.attr('display', 'none')
        hoverCircleOuter.attr('display', 'none')
        tooltip.style('display', 'none')
        return
      }

      /* Find nearest data point by X */
      let nearest = positions[0]
      let minDist = Math.abs(mouseX - nearest.x)
      for (let i = 1; i < positions.length; i++) {
        const dist = Math.abs(mouseX - positions[i].x)
        if (dist < minDist) {
          minDist = dist
          nearest = positions[i]
        }
      }

      /* Show cursor line */
      cursorLine.attr('x1', nearest.x).attr('x2', nearest.x).style('display', 'block')

      /* Show hover circles */
      hoverCircleOuter.attr('cx', nearest.x).attr('cy', nearest.y).attr('display', 'block')
      hoverCircleInner.attr('cx', nearest.x).attr('cy', nearest.y).attr('display', 'block')

      /* Show tooltip */
      const [wrapperMouseX, wrapperMouseY] = d3.pointer(event, wrapperRef.current)
      const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
      const breakdownRows = nearest.data.nistBreakdown
        .map(
          (b) =>
            `<div class='tooltip-row'>` +
            `<div class='tooltip-indicator' style='background:${getNistPillarBackground(
              capitalize(b.name) as SSMNistPillar
            )}'></div>` +
            `<span class='tooltip-label'>${capitalize(b.name)}:</span>` +
            `<span class='tooltip-value'>$${formatCostFull(b.cost)}</span>` +
            `</div>`
        )
        .join('')
      tooltip
        .html(
          `<div class='tooltip-title-row'>` +
            `<span>${nearest.data.label}</span>` +
            `<span>$${formatCostFull(nearest.data.expense)}</span>` +
            `</div>${breakdownRows}`
        )
        .style('display', 'block')

      /* Clamp tooltip within wrapper bounds to prevent scrollbar overflow */
      const ttNode = tooltipRef.current
      let left = wrapperMouseX + 12
      let top = wrapperMouseY - 40
      if (ttNode) {
        const ttWidth = ttNode.offsetWidth
        const ttHeight = ttNode.offsetHeight
        if (left + ttWidth > containerWidth) {
          left = wrapperMouseX - ttWidth - 12
        }
        if (top + ttHeight > containerHeight) {
          top = containerHeight - ttHeight
        }
        if (top < 0) top = 0
      }
      tooltip.style('left', `${left}px`).style('top', `${top}px`)
    })

    svg.on('mouseout', () => {
      cursorLine.style('display', 'none')
      hoverCircleInner.attr('display', 'none')
      hoverCircleOuter.attr('display', 'none')
      tooltip.style('display', 'none')
    })

    /* X-axis - center year labels between Q1 and Q4 */
    const yearGroups = new Map<number, string[]>()
    data.forEach((d) => {
      if (!yearGroups.has(d.year)) yearGroups.set(d.year, [])
      yearGroups.get(d.year)?.push(d.label)
    })

    const xAxisLabels = g.append('g').attr('class', 'x-axis-labels')
    yearGroups.forEach((labels, year) => {
      const firstX = xScale(labels[0]) || 0
      const lastX = xScale(labels[labels.length - 1]) || 0
      const centerX = (firstX + lastX) / 2

      xAxisLabels
        .append('text')
        .attr('x', centerX)
        .attr('y', height + 24)
        .attr('text-anchor', 'middle')
        .attr('fill', getColor('calendar-day'))
        .attr('font-size', '12px')
        .attr('font-weight', 500)
        .attr('font-family', theme.typography.fontFamily.primary)
        .attr('letter-spacing', '0.5px')
        .text(String(year))
    })

    /* Y-axis */
    const yDomain = yScale.domain()
    const yMid = (yDomain[0] + yDomain[1]) / 2
    const yAxis = d3
      .axisLeft(yScale)
      .tickValues([yDomain[0], yMid, yDomain[1]])
      .tickFormat((d) => `$${formatCostCompact(d as number)}`)
      .tickSizeInner(0)
      .tickSizeOuter(0)
      .tickPadding(8)

    const yAxisGroup = g.append('g').attr('class', 'y-axis').call(yAxis)

    yAxisGroup
      .selectAll('text')
      .attr('fill', getColor('calendar-day'))
      .attr('color', getColor('calendar-day'))
      .attr('font-size', '14px')
      .attr('font-weight', 500)
      .attr('font-family', theme.typography.fontFamily.primary)
      .attr('letter-spacing', '0.1px')

    yAxisGroup.select('.domain').remove()
    yAxisGroup.selectAll('line').remove()
  }, [data, dimensions, loading, hasData, getColor])

  return (
    <ChartCard sx={{ height: 352, display: 'flex', flexDirection: 'column' }}>
      <Box className="chart-card-header">
        <Typography className="chart-card-title">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M1.6875 13.4998L6.75 8.43732L9.97982 11.6671C10.8826 9.89091 12.3781 8.40155 14.3402 7.52798L16.3957 6.61282M16.3957 6.61282L11.9397 4.90234M16.3957 6.61282L14.6852 11.0688"
              stroke={theme.colors.neutral}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Expense Trend
        </Typography>
      </Box>
      <ChartWrapper ref={wrapperRef} sx={{ flex: 1, minHeight: 0 }}>
        {loading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ animation: 'spin 1.8s linear infinite' }}
            >
              <path
                d="M20 6.25V10M29.7227 10.2773L27.0711 12.9289M33.75 20H30M12.9289 27.0711L10.2773 29.7227M10 20H6.25M12.9289 12.9289L10.2773 10.2773M20.0002 33.75V30M27.0712 27.0711L29.7229 29.7227"
                stroke="white"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M27.0713 27.0703L29.7229 29.722"
                stroke="#D9CED6"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M33.75 20H30" stroke="#B1A6AE" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              <path
                d="M29.7227 10.2734L27.071 12.9251"
                stroke="#7F747C"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
        ) : !hasData ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              paddingBottom: '40px',
              zIndex: 1
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ flexShrink: 0 }}
            >
              <path
                d="M3 24.0002L12 15.0002L17.7419 20.7421C19.3468 17.5844 22.0056 14.9366 25.4937 13.3836L29.1479 11.7567M29.1479 11.7567L21.2262 8.71582M29.1479 11.7567L26.107 19.6784"
                stroke={getColor('dropdown-label')}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', width: 283 }}>
              <Typography
                sx={{
                  color: getColor('menu-item-text'),
                  fontSize: '16px',
                  fontWeight: 500,
                  fontFamily: theme.typography.fontFamily.primary,
                  letterSpacing: '0.016px',
                  lineHeight: '20px'
                }}
              >
                No Performance Data Is Available Yet
              </Typography>
              <Typography
                sx={{
                  color: getColor('text-secondary'),
                  fontSize: '14px',
                  fontFamily: theme.typography.fontFamily.primary,
                  lineHeight: '20px',
                  textAlign: 'center'
                }}
              >
                Activate CPI on the platform to start tracking your performance and see your metrics here
              </Typography>
            </Box>
          </Box>
        ) : null}
        <ChartTooltip ref={tooltipRef} />
        <svg ref={svgRef} />
      </ChartWrapper>
      {hasData && !loading ? (
        <LegendContainer>
          <LegendItem>
            <Box className="legend-indicator" sx={{ background: RECORDED_COLOR }} />
            <Typography className="legend-label">Recorded</Typography>
          </LegendItem>
          <LegendItem>
            <Box className="legend-indicator" sx={{ background: ESTIMATED_COLOR }} />
            <Typography className="legend-label">Estimated</Typography>
          </LegendItem>
        </LegendContainer>
      ) : null}
    </ChartCard>
  )
}

export default ExpenseTrendChart
